import { create } from 'zustand';
import {
  getApiKeys,
  createApiKey,
  updateApiKey,
  revokeApiKey,
  activateApiKey,
  deleteApiKey,
  getAvailableRoutes,
  ApiKey,
  ApiKeyCreateResponse,
  AvailableRoute,
  Pagination,
} from '@/services/PagesServices/ApiKeyService';

interface ApiKeyState {
  // Data
  apiKeys: ApiKey[];
  availableRoutes: AvailableRoute[];
  pagination: Pagination | null;
  newlyCreatedKey: ApiKeyCreateResponse | null;

  // Loading
  loading: boolean;
  loadingRoutes: boolean;
  creating: boolean;
  updating: boolean;

  // Error
  error: string | null;

  // Filters
  statusFilter: string;
  currentPage: number;

  // Actions
  fetchApiKeys: (page?: number, status?: string) => Promise<void>;
  fetchAvailableRoutes: () => Promise<void>;
  createKey: (data: {
    name: string;
    allowed_routes: string[];
    expires_at?: string | null;
  }) => Promise<{ success: boolean; message: string }>;
  updateKey: (
    uuid: string,
    data: { name?: string; allowed_routes?: string[]; expires_at?: string | null }
  ) => Promise<{ success: boolean; message: string }>;
  revokeKey: (uuid: string) => Promise<{ success: boolean; message: string }>;
  activateKey: (uuid: string) => Promise<{ success: boolean; message: string }>;
  deleteKey: (uuid: string) => Promise<{ success: boolean; message: string }>;
  clearNewKey: () => void;
  setStatusFilter: (status: string) => void;
  setCurrentPage: (page: number) => void;
}

const useApiKeyStore = create<ApiKeyState>((set, get) => ({
  apiKeys: [],
  availableRoutes: [],
  pagination: null,
  newlyCreatedKey: null,

  loading: false,
  loadingRoutes: false,
  creating: false,
  updating: false,

  error: null,

  statusFilter: '',
  currentPage: 1,

  fetchApiKeys: async (page?: number, status?: string) => {
    const state = get();
    set({ loading: true, error: null });

    try {
      const response = await getApiKeys(
        page ?? state.currentPage,
        20,
        (status ?? state.statusFilter) || undefined
      );

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load API keys');
      }

      set({
        apiKeys: response.data || [],
        pagination: response.pagination || null,
        loading: false,
      });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  fetchAvailableRoutes: async () => {
    set({ loadingRoutes: true });

    try {
      const response = await getAvailableRoutes();

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load routes');
      }

      set({
        availableRoutes: response.data || [],
        loadingRoutes: false,
      });
    } catch (err: any) {
      set({ loadingRoutes: false });
    }
  },

  createKey: async (data) => {
    set({ creating: true, error: null });

    try {
      const response = await createApiKey(data);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to create API key');
      }

      set({ creating: false, newlyCreatedKey: response.data || null });
      get().fetchApiKeys();

      return { success: true, message: response.message || 'API key created successfully' };
    } catch (err: any) {
      set({ creating: false });
      return { success: false, message: err.message };
    }
  },

  updateKey: async (uuid, data) => {
    set({ updating: true });

    try {
      const response = await updateApiKey(uuid, data);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to update API key');
      }

      set({ updating: false });
      get().fetchApiKeys();

      return { success: true, message: 'API key updated successfully' };
    } catch (err: any) {
      set({ updating: false });
      return { success: false, message: err.message };
    }
  },

  revokeKey: async (uuid) => {
    try {
      const response = await revokeApiKey(uuid);
      if (!response.success) throw new Error(response.error?.message);
      get().fetchApiKeys();
      return { success: true, message: response.message || 'API key revoked' };
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  },

  activateKey: async (uuid) => {
    try {
      const response = await activateApiKey(uuid);
      if (!response.success) throw new Error(response.error?.message);
      get().fetchApiKeys();
      return { success: true, message: response.message || 'API key activated' };
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  },

  deleteKey: async (uuid) => {
    try {
      const response = await deleteApiKey(uuid);
      if (!response.success) throw new Error(response.error?.message);
      get().fetchApiKeys();
      return { success: true, message: response.message || 'API key deleted' };
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  },

  clearNewKey: () => set({ newlyCreatedKey: null }),
  setStatusFilter: (status) => set({ statusFilter: status, currentPage: 1 }),
  setCurrentPage: (page) => set({ currentPage: page }),
}));

export default useApiKeyStore;
